import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { InboxArrowDownIcon, XMarkIcon, CheckBadgeIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useInbox, useInboxMessage, useAcknowledge } from '../hooks/useComms';
import api from '../services/api';
import MessageBlocks from './MessageBlocks';
import AddressConfirmPanel from './AddressConfirmPanel';
import DocumentViewer from './DocumentViewer';
import { deptLabel, splitByDepartment } from './commsDepartments';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const parseBlocks = (s) => { try { return JSON.parse(s); } catch { return []; } };

// A message with a "complete by" date. Done means acknowledged where that is
// asked for, otherwise opened; once done the deadline no longer nags.
function DueChip({ m }) {
  if (!m.due_at) return null;
  const done = m.requires_ack ? !!m.acknowledged_at : !!m.read_at;
  if (done) return null;
  const overdue = new Date(m.due_at) < new Date();
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${overdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
      {overdue ? 'Overdue' : `Due ${fmtDate(m.due_at)}`}
    </span>
  );
}

// Approve/Deny an Accreditation Training Request from inside the inbox.
function ApprovalPanel({ actionRef, onDone }) {
  const [reqData, setReqData] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/api/training-requests/${actionRef}`).then((r) => { if (live) setReqData(r.data); }).catch(() => { if (live) setErr('Could not load request'); });
    return () => { live = false; };
  }, [actionRef]);
  const decide = async (decision) => {
    setBusy(true); setErr(null);
    try { await api.post(`/api/training-requests/${actionRef}/decide`, { decision, note: note || null }); onDone(); }
    catch (e) { setErr(e?.response?.data?.error || 'Failed'); setBusy(false); }
  };
  if (err && !reqData) return <p className="mt-4 text-sm text-red-500">{err}</p>;
  if (!reqData) return <p className="mt-4 text-sm text-gray-400">Loading request…</p>;
  const done = reqData.status === 'approved' || reqData.status === 'rejected';
  return (
    <div className="mt-5 rounded-lg border border-gray-200 p-4">
      <dl className="space-y-1 text-sm">
        <div><span className="text-gray-400">Engineer:</span> <span className="font-medium text-gray-800">{reqData.subject_name}</span></div>
        <div><span className="text-gray-400">Accreditation:</span> {reqData.accreditation_labels}</div>
        {reqData.expiry_date && <div><span className="text-gray-400">Expiry:</span> {fmtDay(reqData.expiry_date)}</div>}
        {(reqData.job_title || reqData.region || reqData.cluster) && <div className="text-gray-500">{[reqData.job_title, reqData.region, reqData.cluster].filter(Boolean).join(' · ')}</div>}
        {reqData.notes && <div><span className="text-gray-400">Notes:</span> {reqData.notes}</div>}
        <div><span className="text-gray-400">Requested by:</span> {reqData.requested_by_name}</div>
      </dl>
      {done ? (
        <p className={`mt-3 text-sm font-medium ${reqData.status === 'approved' ? 'text-emerald-600' : 'text-red-600'}`}>Request {reqData.status}.</p>
      ) : reqData.can_decide ? (
        <>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (optional)"
            className="mt-3 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-brand focus:outline-none" />
          <div className="mt-3 flex gap-2">
            <button type="button" disabled={busy} onClick={() => decide('rejected')}
              className="flex-1 rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">Deny</button>
            <button type="button" disabled={busy} onClick={() => decide('approved')}
              className="flex-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Approve</button>
          </div>
          {err && <p className="mt-2 text-sm text-red-500">{err}</p>}
        </>
      ) : (
        <p className="mt-3 text-sm text-gray-500">Waiting on {reqData.current_approver || 'the approver'}.</p>
      )}
    </div>
  );
}

function MessageModal({ id, onClose }) {
  const { data: msg, isLoading } = useInboxMessage(id);
  const ack = useAcknowledge();
  const qc = useQueryClient();
  const [viewingDoc, setViewingDoc] = useState(null);
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      {viewingDoc && <DocumentViewer block={viewingDoc} commId={id} onClose={() => setViewingDoc(null)} />}
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Message</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
        {msg && (
          <>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand">{deptLabel(msg.department)}</p>
            <h3 className="text-base font-semibold text-gray-900">
              {msg.priority === 'high' && <span className="mr-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600">High</span>}
              {msg.subject}
            </h3>
            <p className="mt-1 text-xs text-gray-400">From {msg.sender_name || 'Nano'} · {fmtDate(msg.created_at)}</p>
            {msg.due_at && (
              <p className={`mt-1 text-xs font-medium ${new Date(msg.due_at) < new Date() && !(msg.requires_ack ? msg.acknowledged_at : msg.read_at) ? 'text-red-600' : 'text-gray-600'}`}>
                Complete by {fmtDate(msg.due_at)}
              </p>
            )}
            {!!msg.requires_ack && msg.action_type !== 'address_confirm' && !msg.acknowledged_at && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                This message requires your acknowledgement after reading it.
              </div>
            )}
            {msg.format === 'blocks'
              ? <div className="mt-4"><MessageBlocks blocks={parseBlocks(msg.body)} commId={id} onOpenDocument={setViewingDoc} /></div>
              : <p className="mt-4 whitespace-pre-wrap text-sm text-gray-700">{msg.body}</p>}
            {msg.action_type === 'training_approval' && Number(msg.action_ref) > 0 && (
              <ApprovalPanel actionRef={msg.action_ref} onDone={() => { qc.invalidateQueries({ queryKey: ['commInbox'] }); onClose(); }} />
            )}
            {/* The inbox copy of an IT reply is read-only; the conversation and
                the reply box live on the ticket. */}
            {msg.action_type === 'it_ticket' && Number(msg.action_ref) > 0 && (
              <Link
                to={`/helpdesk?ticket=${msg.action_ref}`}
                className="mt-5 flex w-full items-center justify-center rounded-md bg-brand px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-dark"
              >
                Open the ticket to reply
              </Link>
            )}
            {msg.action_type === 'address_confirm' && Number(msg.action_ref) > 0 && (
              <AddressConfirmPanel requestId={msg.action_ref} />
            )}
            {!!msg.requires_ack && msg.action_type !== 'address_confirm' && (
              <div className="mt-6 border-t border-gray-100 pt-4">
                {msg.acknowledged_at ? (
                  <p className="flex items-center gap-1.5 text-sm font-medium text-emerald-600"><CheckBadgeIcon className="h-5 w-5" /> Acknowledged {fmtDate(msg.acknowledged_at)}</p>
                ) : (
                  <>
                    <button type="button" onClick={() => ack.mutate(id)} disabled={ack.isPending}
                      className="w-full rounded-md bg-brand px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50">
                      {ack.isPending ? 'Saving…' : 'I acknowledge I have read this'}
                    </button>
                    {ack.isError && (
                      <p className="mt-2 text-sm text-red-600">
                        {ack.error?.response?.data?.error || 'Could not save your acknowledgement.'}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function MessageRow({ m, onOpen }) {
  return (
    <button type="button" onClick={onOpen}
      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left shadow-sm hover:border-brand ${m.read_at ? 'border-gray-200 bg-white' : 'border-brand/40 bg-brand-light/30'}`}>
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          {!m.read_at && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" />}
          {m.priority === 'high' && <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600">High</span>}
          <span className={`truncate text-sm ${m.read_at ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>{m.subject}</span>
          {!!m.requires_ack && !m.acknowledged_at && <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">{m.action_type === 'address_confirm' ? 'Reply needed' : 'Ack needed'}</span>}
          <DueChip m={m} />
          {m.acknowledged_at && <CheckBadgeIcon className="h-4 w-4 shrink-0 text-emerald-500" />}
        </span>
        <span className="mt-0.5 block truncate text-xs text-gray-400">From {m.sender_name || 'Nano'} · {fmtDate(m.created_at)}</span>
      </span>
    </button>
  );
}

// One tab per department header. `count` is unread, so a tab with something new
// in it stands out; a header with nothing in it is dimmed but stays in place.
function DeptTab({ label, count, total, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${active ? 'border-brand bg-brand text-white' : total ? 'border-gray-300 bg-white text-gray-700 hover:border-brand hover:text-brand' : 'border-gray-200 bg-white text-gray-300'}`}>
      {label}
      {count > 0 && <span className={`rounded-full px-1.5 text-[10px] font-semibold ${active ? 'bg-white text-brand' : 'bg-brand text-white'}`}>{count}</span>}
    </button>
  );
}

function CommsInbox() {
  const navigate = useNavigate();
  const { data: messages, isLoading } = useInbox();
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState('all');
  const sections = useMemo(() => splitByDepartment(messages), [messages]);
  const unreadTotal = sections.reduce((s, d) => s + d.unread, 0);
  const current = sections.find((d) => d.key === tab);
  // Deep link (/messages/:id from the email button) auto-opens that message;
  // opening it marks it read via the same hook the manual open uses.
  const { id } = useParams();
  useEffect(() => {
    if (id) setOpenId(Number(id));
  }, [id]);

  return (
    <div className="p-6">
      <button type="button" onClick={() => navigate('/')} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to dashboard
      </button>
      <h1 className="mb-1 flex items-center gap-2 text-2xl font-bold text-gray-900"><InboxArrowDownIcon className="h-7 w-7 text-brand" /> Inbox</h1>
      <p className="mb-6 text-sm text-gray-500">Communications sent to you. Open one to read it{`; some ask you to acknowledge.`}</p>

      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {messages && (
        <>
          <div className="mb-5 flex flex-wrap gap-2">
            <DeptTab label="All" count={unreadTotal} total={messages.length} active={tab === 'all'} onClick={() => setTab('all')} />
            {sections.map((d) => (
              <DeptTab key={d.key} label={d.label} count={d.unread} total={d.messages.length} active={tab === d.key} onClick={() => setTab(d.key)} />
            ))}
          </div>

          {messages.length === 0 && <p className="text-sm text-gray-400">No messages yet.</p>}

          {/* All: every non-empty department as its own section, in header order. */}
          {tab === 'all' && messages.length > 0 && (
            <div className="space-y-6">
              {sections.filter((d) => d.messages.length).map((d) => (
                <section key={d.key}>
                  <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {d.label}
                    <span className="font-normal normal-case tracking-normal text-gray-400">{d.messages.length}{d.unread ? ` · ${d.unread} unread` : ''}</span>
                  </h2>
                  <div className="space-y-2">
                    {d.messages.map((m) => <MessageRow key={m.id} m={m} onOpen={() => setOpenId(m.id)} />)}
                  </div>
                </section>
              ))}
            </div>
          )}

          {tab !== 'all' && current && (
            <div className="space-y-2">
              {current.messages.map((m) => <MessageRow key={m.id} m={m} onOpen={() => setOpenId(m.id)} />)}
              {current.messages.length === 0 && <p className="text-sm text-gray-400">No {current.label} messages.</p>}
            </div>
          )}
        </>
      )}
      {openId && <MessageModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

export default CommsInbox;
